import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { useFormState } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  ResolvedLoanFragmentGQLType,
  GetTasksDocumentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { EditLoanFormFields } from '../types';
import { useEditLoanContext } from '../useEditLoanContext';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { successToastConfig } from '~/packages/common-frontend/constants';
import { useApproveAndExecuteLoanEditMutation } from '~/packages/loans/requests/loans.gql.generated';

interface ConfirmEditSubmitModalProps {
  modalControls: ReturnType<typeof useDisclosure>;
  loan: ResolvedLoanFragmentGQLType;
}
export const ConfirmEditSubmitModal = ({
  modalControls,
  loan,
}: ConfirmEditSubmitModalProps) => {
  const formState = useFormState<EditLoanFormFields>();

  return (
    <Modal
      isOpen={modalControls.isOpen}
      onClose={modalControls.onClose}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>Confirm Submission</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Typography variant="detail-muted" TextProps={{ fontSize: '16px' }}>
            Are you sure you want to submit the loan edits for{' '}
            {loan.name || loan.facility.creditFacilityName}?
          </Typography>

          <Flex justify="flex-end" align="center" gap="1rem" mt="2rem">
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={modalControls.onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              colorScheme="v2-aurora"
              type="submit"
              form="edit-loan"
              isLoading={formState.isSubmitting}
            >
              Submit
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

interface ConfirmEditApproveModalProps {
  modalControls: ReturnType<typeof useDisclosure>;
}
export const ConfirmEditApproveModal = ({
  modalControls,
}: ConfirmEditApproveModalProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useUserAuthOrThrow();

  const context = useEditLoanContext();

  const [
    approveAndExecuteEditApi,
    approveAndExecuteMutation,
  ] = useApproveAndExecuteLoanEditMutation({
    refetchQueries: [GetTasksDocumentGQLType],
  });
  if (approveAndExecuteMutation.error) throw approveAndExecuteMutation.error;

  if (context.status !== 'ready' || !context.state.newEntities) {
    return null;
  }

  return (
    <Modal
      isOpen={modalControls.isOpen}
      onClose={modalControls.onClose}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>Confirm Submission</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Typography variant="detail-muted" TextProps={{ fontSize: '16px' }}>
            Are you sure you want to approve the loan edits for{' '}
            {context.state.originalResolvedLoan.name ||
              context.state.originalResolvedLoan.facility.creditFacilityName}
            ?
          </Typography>

          <Flex justify="flex-end" align="center" gap="1rem" mt="2rem">
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={modalControls.onClose}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              colorScheme="v2-aurora"
              isLoading={approveAndExecuteMutation.loading}
              onClick={async () => {
                if (!context.state.newEntities) return;

                await approveAndExecuteEditApi({
                  variables: {
                    originalLoanId: context.state.originalResolvedLoan.id,
                    newLoanId: context.state.newEntities.newResolvedLoan.id,
                    loanActionId: context.state.newEntities.loanAction.id,
                    approvedUserId: user.id, // TODO: @markhaghani, set up guardrails?
                  },
                });

                toast({
                  ...successToastConfig,
                  duration: 3000,
                  title: 'Successfully approved loan edit',
                });

                navigate(
                  `/loans/${context.state.newEntities.newResolvedLoan.id}`,
                );
              }}
            >
              Approve
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
